import { Button, Stack, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Page } from 'theme/elements/Page';
import { FormCardSingleColumnLayout } from 'theme/elements/Forms';
import { FormButton } from 'theme/elements/Buttons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { paymentCategoryForm } from '../forms.types.create';
import FormCreatorComponent from 'components/FormCreatorComponent';
import {
	getPaymentCategories as getPaymentCategoriesSelector,
	getCurrentCategory,
	setCurrentCategory,
	getPaymentCategoriesTranslationsMap,
	getPaymentCategoriesError,
	setError,
} from 'state/reducers/payment-categories.reducer';
import {
	getPaymentCategory,
	createPaymentCategory,
	updatePaymentCategory,
	getPaymentCategories,
	fetchPaymentCategoryTranslations,
} from 'services/payment-categories.service';
import { getPaymentsPurpose } from 'services/global.service';
import { getPaymentsPurpose as getPaymentsPurposeSelector } from 'state/reducers/global.reducer';
import { WarningDialog } from 'components/WarningDialog';
import { IPaymentCategory, IPaymentCategoryFormData } from 'constants/payment-categories.constants';
import { pagesEnum, initialMessage } from 'constants/ui.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { PurposeCodeOption } from 'components/PurposeCodePicker/types';

/**
 * Helper to flatten category tree for parent selection dropdown
 * Uses translationsMap to get Hebrew name, otherwise falls back to category name
 * Excludes categories that don't allow children
 */
const flattenCategories = (
	categories: IPaymentCategory[],
	translationsMap: Record<string, { en?: string; he?: string }> | undefined,
	excludeId?: number,
	prefix = ''
): { value: number | string; label: string }[] => {
	let result: { value: number | string; label: string }[] = [];

	for (const category of categories) {
		// Exclude the current category and its children when editing
		if (excludeId && category.id === excludeId) continue;

		// Skip categories that don't allow children
		if (category.allowChildren === false) continue;

		const key = `payment_category.name.${category.name}`;
		result.push({
			value: category.id,
			label: prefix + (translationsMap?.[key]?.he || category.name),
		});

		if (category.children && category.children.length > 0) {
			result = result.concat(flattenCategories(category.children, translationsMap, excludeId, prefix + '-- '));
		}
	}

	return result;
};

export const UpsertPaymentCategory: React.FC = () => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const parentIdParam = searchParams.get('parentId');

	const [formState, setFormState] = useState<Record<string, any>>({});
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [isSaving, setIsSaving] = useState(false);
	const initialFormValuesRef = useRef<Record<string, any> | null>(null);

	const categories = useAppSelector(getPaymentCategoriesSelector);
	const currentCategory = useAppSelector(getCurrentCategory);
	const translationsMap = useAppSelector(getPaymentCategoriesTranslationsMap);
	const errorState = useAppSelector(getPaymentCategoriesError);
	const paymentsPurpose = useAppSelector(getPaymentsPurposeSelector);

	const isNew = id === 'new';
	const prefix = 'page.payment_categories.';
	// Use EDIT_PAYMENTS_PURPOSE permission as it covers payment-related management
	const editPermissions = hasPermission([permissionEnums.EDIT_PAYMENTS_PURPOSE]);

	// Error dialog handling - following SystemSettings.tsx pattern
	const errorDialogOpen = useMemo(() => {
		return errorState?.exist || false;
	}, [errorState?.exist]);

	const errorDialogMessage = useMemo(() => {
		if (errorState?.exist && errorState?.text) {
			return errorState.text;
		}
		return '';
	}, [errorState?.exist, errorState?.text]);

	const handleErrorDialogClose = () => {
		dispatch(setError(initialMessage));
	};

	// Load category data if editing
	useEffect(() => {
		if (!isNew && id) {
			dispatch(getPaymentCategory(Number(id)));
		} else {
			// Clear current category when creating new
			dispatch(setCurrentCategory(null));
		}

		// Cleanup on unmount
		return () => {
			dispatch(setCurrentCategory(null));
		};
	}, [id, isNew, dispatch]);

	// Load payment purpose codes for dropdown
	useEffect(() => {
		if (!paymentsPurpose) {
			dispatch(getPaymentsPurpose());
		}
	}, [dispatch, paymentsPurpose]);

	// Load categories and translations if not already loaded (for direct navigation to edit page)
	useEffect(() => {
		if (categories.length === 0) {
			dispatch(getPaymentCategories() as any);
		}
		if (!translationsMap || Object.keys(translationsMap).length === 0) {
			dispatch(fetchPaymentCategoryTranslations() as any);
		}
	}, [dispatch, categories.length, translationsMap]);

	// Set initial form values (including paymentPurposeCodeIds)
	// Translations are now included in the API response from getCategoryById
	useEffect(() => {
		if (isNew) {
			const newFormValues = {
				name: '',
				parentId: parentIdParam ? Number(parentIdParam) : '',
				description: '',
				nameEn: '',
				nameHe: '',
				paymentPurposeCodeIds: [],
				disclaimerEn: '',
				disclaimerHe: '',
				isActive: '1',
			};
			initialFormValuesRef.current = newFormValues;
			setFormValues(newFormValues);
		} else if (currentCategory) {
			const nameKey = currentCategory.name ? `payment_category.name.${currentCategory.name}` : '';
			const disclaimerKey = currentCategory.name ? `payment_category.disclaimer.${currentCategory.name}` : '';
			// Extract payment purpose code IDs from junction table data
			const paymentPurposeCodeIds =
				currentCategory.categoryPurposeCodes?.map((cpc: any) => cpc.paymentPurposeCodeId) ||
				currentCategory.paymentPurposeCodeIds ||
				[];
			const newFormValues = {
				name: currentCategory.name || '',
				parentId: currentCategory.parentId || '',
				description: currentCategory.description || '',
				nameEn: translationsMap?.[nameKey]?.en || currentCategory.nameEn || '',
				nameHe: translationsMap?.[nameKey]?.he || currentCategory.nameHe || '',
				paymentPurposeCodeIds,
				disclaimerEn: translationsMap?.[disclaimerKey]?.en || currentCategory.disclaimerEn || '',
				disclaimerHe: translationsMap?.[disclaimerKey]?.he || currentCategory.disclaimerHe || '',
				isActive: currentCategory.isActive ? '1' : '0',
			};
			initialFormValuesRef.current = newFormValues;
			setFormValues(newFormValues);
		}
	}, [isNew, currentCategory, parentIdParam, translationsMap]);

	// Custom dirty check - uses FormCreatorComponent's isDirty flag with deep comparison
	const isActuallyDirty = useMemo(() => {
		// FormCreatorComponent tracks isDirty internally after first change
		if (formState.isDirty) {
			// If form says dirty, do deep comparison for accuracy
			if (formState.values && initialFormValuesRef.current) {
				const current = {
					...formState.values,
					parentId: String(formState.values.parentId || ''),
					paymentPurposeCodeIds: [...(formState.values.paymentPurposeCodeIds || [])].sort(),
				};
				const initial = {
					...initialFormValuesRef.current,
					parentId: String(initialFormValuesRef.current.parentId || ''),
					paymentPurposeCodeIds: [...(initialFormValuesRef.current.paymentPurposeCodeIds || [])].sort(),
				};
				return !_.isEqual(current, initial);
			}
			return true;
		}
		return false;
	}, [formState.isDirty, formState.values]);

	// Build purpose code options for the multi-select picker
	const purposeCodePickerOptions: PurposeCodeOption[] = useMemo(() => {
		if (!paymentsPurpose || !Array.isArray(paymentsPurpose)) return [];
		return paymentsPurpose.map((pc: any) => ({
			id: pc.id || pc.value,
			code: pc.code,
			countryName: pc.country?.name || '',
			currenciesCode: pc.currencies_code || '',
			description: pc.description,
		}));
	}, [paymentsPurpose]);

	// Build form with dynamic options
	const form = useMemo(() => {
		const _form = _.cloneDeep(paymentCategoryForm) as any;

		// Build parent category options (exclude current category when editing)
		const parentOptions = [
			{ value: '', label: t('page.payment_categories.field.no_parent') },
			...flattenCategories(categories, translationsMap, isNew ? undefined : Number(id)),
		];
		_form.fieldsGroups[0].fields[1].props.options = parentOptions;

		// Disable parent field when editing (changing parent not supported)
		if (!isNew) {
			_form.fieldsGroups[0].fields[1].props.disabled = true;
		}

		// Set purpose code picker customData (group index 2, field index 0)
		_form.fieldsGroups[2].fields[0].props.customData = {
			options: purposeCodePickerOptions,
			loading: !paymentsPurpose,
			noOptions: paymentsPurpose && paymentsPurpose.length === 0,
		};

		// Disable purpose code picker if no edit permissions
		if (!editPermissions) {
			_form.fieldsGroups[2].fields[0].props.disabled = true;
		}

		return _form;
	}, [categories, id, isNew, t, translationsMap, purposeCodePickerOptions, paymentsPurpose, editPermissions]);

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const values = formState.values || {};
			const paymentPurposeCodeIds = values.paymentPurposeCodeIds || [];

			let success = false;
			if (isNew) {
				// Create includes parentId
				const createData: IPaymentCategoryFormData = {
					name: values.name,
					parentId: values.parentId ? Number(values.parentId) : null,
					description: values.description || undefined,
					nameEn: values.nameEn || undefined,
					nameHe: values.nameHe || undefined,
					disclaimerEn: values.disclaimerEn || undefined,
					disclaimerHe: values.disclaimerHe || undefined,
					isActive: values.isActive === '1',
					paymentPurposeCodeIds: paymentPurposeCodeIds.length > 0 ? paymentPurposeCodeIds : undefined,
				};
				success = await dispatch(createPaymentCategory(createData));
			} else {
				// Update excludes parentId (changing parent not supported)
				const updateData: IPaymentCategoryFormData = {
					name: values.name,
					description: values.description || undefined,
					nameEn: values.nameEn || undefined,
					nameHe: values.nameHe || undefined,
					disclaimerEn: values.disclaimerEn || undefined,
					disclaimerHe: values.disclaimerHe || undefined,
					isActive: values.isActive === '1',
					paymentPurposeCodeIds,
				};
				success = await dispatch(updatePaymentCategory(Number(id), updateData));
			}

			if (success) {
				dispatch(setSuccessMessage(true));
				navigate(`/${pagesEnum.PAYMENT_CATEGORIES}`);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = () => {
		navigate(`/${pagesEnum.PAYMENT_CATEGORIES}`);
	};

	return (
		<Fragment>
			<Page includeGoBack title={t(`${prefix}${isNew ? 'new' : 'edit'}.title`)}>
				{/* Error Dialog for API errors */}
				<WarningDialog
					open={errorDialogOpen}
					subTitle={t(errorDialogMessage)}
					handleCancel={handleErrorDialogClose}
				/>
				<FormCardSingleColumnLayout>
					<FormCreatorComponent
						{...form}
						translation={t}
						values={formValues}
						checkInitValidation={true}
						handleChange={(newFormState: Record<string, any>) => {
							setFormState(newFormState);
						}}
					/>
					<Stack direction="row" justifyContent="space-between" width="100%">
						<FormButton>
							<Button onClick={handleCancel} variant="outlined">
								{t('button.cancel')}
							</Button>
						</FormButton>
						{editPermissions && (
							<FormButton>
								<Button
									variant="contained"
									onClick={handleSave}
									disabled={!formState.isValid || !isActuallyDirty || isSaving}
									startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
								>
									{t('label.save')}
								</Button>
							</FormButton>
						)}
					</Stack>
				</FormCardSingleColumnLayout>
			</Page>
		</Fragment>
	);
};

export default UpsertPaymentCategory;
